// @ts-nocheck
import { formatDistanceToNow } from "date-fns";
import { combineLatest, from, of } from "rxjs";
import { concatMap, filter, map, switchMap } from "rxjs/operators";
import { setState, state } from "..";
import { isInstalled, isNative } from "../../services/Device/device";

import {
  achievementsObservable,
  chaptersObservable,
  gamificationObservable,
  levelByNameObservable,
  lockersObservable,
  rewardsObservable,
  actionsByListIdsObservable,
  challengesByListIdsObservable,
} from "../../services/Gamification/Gamification";
import {
  getUserPastPendingActions,
  updateUserProgress,
  userChallengeListObservable,
  userProfileObservable,
} from "../../services/User/User";
import { getLocale } from "../../utils/i8n";
import { stringCapitalize } from "../../utils/string";
import { reportError } from "../error/effects";
import { GameNotifications, PastPendingActions } from "./state";
import notificationsData from '../../content/app/data/app_notifications.json';
import { replace as Rreplace } from "ramda";

function subscribeToGameNotificationsEffect() {
  //TODO: Improve filters on fetched data
  const effect = "subscribeToGameNotificationsEffect";
  const userProfile$ = userProfileObservable(state.USER.userId);
  const level$ = (name: string) =>
    levelByNameObservable(name, state.DEVICE.languageCode);
  const achievement$ = achievementsObservable(state.DEVICE.languageCode);
  const reward$ = rewardsObservable(state.DEVICE.languageCode);
  const chapter$ = chaptersObservable(state.DEVICE.languageCode);
  const locker$ = lockersObservable();
  const gamification$ = gamificationObservable();
  const installed$ = from(isInstalled());
  const native$ = from(isNative());

  return combineLatest([userProfile$, installed$])
    .pipe(
      filter(
        ([userProfile, installed]) =>
          userProfile.status === "completed" && installed
      ),
      concatMap(([userProfile]) =>
        combineLatest([
          level$(userProfile.level),
          userChallengeListObservable(userProfile.userId),
          achievement$,
          reward$,
          chapter$,
          locker$,
          gamification$,
        ]).pipe(
          concatMap(
            ([
              level,
              userChallenges,
              achievements,
              rewards,
              chapters,
              lockers,
              gamification,
            ]) => {
              return native$.pipe(
                concatMap((native) =>
                  native
                    ? from(
                      import("../../services/Gamification/GameEngine")
                    ).pipe(
                      concatMap(({ computeGameState }) =>
                        from(
                          computeGameState({
                            userProfile,
                            level,
                            userChallenges,
                            achievements,
                            rewards,
                            chapters,
                            gamification,
                            lockers,
                            languageCode: state.DEVICE.languageCode,
                          })
                        ).pipe(
                          map((gameUpdates) => ({ gameUpdates, userProfile }))
                        )
                      )
                    )
                    : from(import("./gamification.compute")).pipe(
                      concatMap(({ computeGameState }) =>
                        from(
                          computeGameState({
                            userProfile,
                            level,
                            userChallenges,
                            achievements,
                            rewards,
                            chapters,
                            gamification,
                            lockers,
                            languageCode: state.DEVICE.languageCode,
                          })
                        ).pipe(
                          map((gameUpdates) => ({ gameUpdates, userProfile }))
                        )
                      )
                    )
                )
              );
            }
          )
        )
      ),
      concatMap(({ gameUpdates, userProfile }) => {
        setState(
          "GAME_NOTIFICATIONS",
          {
            GAME_NOTIFICATIONS: {
              progress: [
                ...state.GAME_NOTIFICATIONS.progress,
                ...gameUpdates.notifications.progress,
              ],
              achievements: [
                ...state.GAME_NOTIFICATIONS.achievements,
                ...gameUpdates.notifications.achievements
              ],
              pastPendingActions: undefined,
            },
          },
          effect
        );
        return updateUserProgress(gameUpdates, userProfile);
      })
    )
    .subscribe({
      error(err) {
        reportError("GAME_NOTIFICATIONS", err, effect);
      },
    });
}

function clearGameNotificationsEffect(
  target: keyof GameNotifications = null
) {
  const effect = "clearGameNotificationsEffect";
  switch (target) {
    case "achievements":
      setState(
        "GAME_NOTIFICATIONS",
        {
          GAME_NOTIFICATIONS: { ...state.GAME_NOTIFICATIONS, achievements: [] },
        },
        effect
      );
      break;
    case "progress":
      setState(
        "GAME_NOTIFICATIONS",
        { GAME_NOTIFICATIONS: { ...state.GAME_NOTIFICATIONS, progress: [] } },
        effect
      );
      break;
    case 'pastPendingActions':
      setState(
        "GAME_NOTIFICATIONS",
        { GAME_NOTIFICATIONS: { ...state.GAME_NOTIFICATIONS, pastPendingActions: null } },
        effect
      );
      break;
    default:
      setState(
        "GAME_NOTIFICATIONS",
        {
          GAME_NOTIFICATIONS: {
            ...state.GAME_NOTIFICATIONS,
            achievements: [],
            progress: [],
            pastPendingActions: null
          },
        },
        effect
      );
      break;
  }
}

function notifyPastPendingActionsEffect() {
  const effect = "notifyPastPendingActionsEffect";
  return getUserPastPendingActions(state.USER.userId)
    .pipe(
      switchMap((pendingActions) => {
        const challengeIds = pendingActions.map((pa) => pa.challengeId);
        const actionIds = pendingActions
          .map((pa) => pa.actions)
          .flat()
          .map((c) => c.actionId);
        const challenge$ = challengesByListIdsObservable(
          challengeIds,
          state.DEVICE.languageCode
        );
        const actions$ = actionsByListIdsObservable(
          actionIds,
          state.DEVICE.languageCode
        );
        return combineLatest([actions$, challenge$, of(pendingActions)]);
      }),
      map(([actions, challenges, pendingActions]) => {
        const locale = getLocale(state.DEVICE.languageCode);
        //TODO: Translate the pending modal text
        const notificationsShell: typeof notificationsData['EN'] = notificationsData[state.DEVICE.languageCode];
        const pendingActionNotification: PastPendingActions = {
          pendingTitle: notificationsShell.pendingActions.pendingTitle,
          pendingDescription: Rreplace(
            "{0}",
            `+${pendingActions.map((pa) => pa.actions).flat().length}`,
            notificationsShell.pendingActions.pendingDescription
          ),
          actionsCounts: pendingActions.map((pa) => pa.actions).flat().length,
          missedAllLabel: notificationsShell.pendingActions.missedAllLabel,
          submitDoneLabel: notificationsShell.pendingActions.submitDoneLabel,
          doneLabel: notificationsShell.pendingActions.doneLabel,
          actionLabel: notificationsShell.pendingActions.actionLabel,
          actions: [...pendingActions
            .map((pa) => {
              const challengeActions = pa.actions.filter((sa) =>
                actions.map((a) => a.id).includes(sa.actionId)
              );
              const challenge = challenges.find(
                (ch) => ch.id === pa.challengeId
              );
              return challengeActions.map((ca) => {
                const missedAction = actions.find(
                  (ac) => ac.id === ca.actionId
                );
                return {
                  actionTitle: missedAction.title,
                  challengeTitle: challenge.title,
                  tags: [],
                  imgUrl: missedAction.coverImg.url,
                  challengeId: ca.challengeId,
                  actionDate: ca.actionDate,
                  ageText: stringCapitalize(
                    formatDistanceToNow(new Date(ca.actionDate), {
                      locale,
                    })
                  ),
                };
              });
            })
            .flat()],
        };
        return pendingActionNotification;
      })
    )
    .subscribe({
      next(pendingActionNotification) {
        setState(
          "GAME_NOTIFICATIONS",
          {
            GAME_NOTIFICATIONS: {
              ...state.GAME_NOTIFICATIONS,
              pastPendingActions: {
                ...pendingActionNotification,
              },
            },
          },
          effect
        );
      },
      error(err) {
        reportError("USER", err, effect);
      },
    });
}

export {
  subscribeToGameNotificationsEffect,
  clearGameNotificationsEffect,
  notifyPastPendingActionsEffect,
};
